/**
 * The microsite's view shapes (src/lib/views.ts there), built from the event
 * payload instead of Neon. MP is the source for what staff change: the run of
 * show (Agenda_Elements), topic titles, rooms, speakers, caps and counts, the
 * lunch answers. content.ts keeps what MP has no home for: slugs, taglines,
 * bundled portraits, blurbs it never got, panel flags, caterer photos.
 *
 * A topic is one talk taught in both sessions, so MP's per-session option
 * prices fold into one TopicView with a slot each, matched through the
 * config's optionPriceIds (and by title for any option the config never knew).
 */
import type { AgendaRow, ChoicePerson, EventDetailsResponse, Topic } from '@/lib/types';
import type {
  AgendaItem,
  AgendaKind,
  BreakoutReplay,
  ConferenceConfig,
  LunchOption,
} from './conference';
import { art } from './art';

export interface SpeakerView {
  name: string;
  tagline: string;
  imageGuid: string | null;
  /** A bundled portrait or an MP file URL, ready to put in src. */
  imageSrc: string | null;
}

export interface TopicView {
  title: string;
  slug: string;
  shortTitle: string;
  subtitle?: string;
  blurb: string;
  panel?: boolean;
  room?: string;
  speaker: SpeakerView;
  replay?: BreakoutReplay;
  slots: {
    slot: number;
    label: string;
    timeHint: string;
    capacity: number;
    picked: number | null;
    /** Product_Option_Price_ID: what choosing this writes. */
    optionPriceId: number;
    /** Product_Option_Group_ID of the session. */
    groupId: number;
  }[];
}

const KINDS: AgendaKind[] = [
  'doors',
  'worship',
  'session',
  'breakout',
  'meal',
  'close',
  'travel',
  'break',
  'reception',
];

const hhmm = (t: string | null) => (t ? t.slice(0, 5) : '');

function splitTitle(title: string): { shortTitle: string; subtitle?: string } {
  const i = title.indexOf(':');
  if (i < 0) return { shortTitle: title.trim() };
  return { shortTitle: title.slice(0, i).trim(), subtitle: title.slice(i + 1).trim() || undefined };
}

/** Speakers by config key, with MP's face where the config has no portrait. */
export function buildSpeakers(
  config: ConferenceConfig,
  details: EventDetailsResponse
): Record<string, SpeakerView> {
  const faces = new Map<number, string | null>();
  for (const r of details.page?.agenda ?? [])
    if (r.speaker) faces.set(r.speaker.contactId, r.speaker.imageUrl);
  for (const t of details.page?.topics ?? [])
    if (t.speaker) faces.set(t.speaker.contactId, t.speaker.imageUrl);
  const map: Record<string, SpeakerView> = {};
  for (const s of config.speakers) {
    map[s.key] = {
      name: s.name,
      tagline: s.tagline,
      imageGuid: null,
      imageSrc: s.imageSrc
        ? art(s.imageSrc)
        : s.contactId
          ? (faces.get(s.contactId) ?? null)
          : null,
    };
  }
  return map;
}

/** Which config speaker an MP contact is, by Contact_ID then by name. */
function speakerKeyFor(
  config: ConferenceConfig,
  person: { contactId: number; name: string } | null
): string | undefined {
  if (!person) return undefined;
  const byId = config.speakers.find((s) => s.contactId === person.contactId);
  if (byId) return byId.key;
  const name = person.name.toLowerCase();
  return config.speakers.find((s) => s.name.toLowerCase() === name)?.key;
}

/** Breakout session number for an option group, from the config's groups. */
export function slotForGroup(config: ConferenceConfig, groupId: number | null): number | undefined {
  if (!groupId) return undefined;
  return config.breakoutGroups.find((g) => g.groupId === groupId)?.slot;
}

export function buildAgenda(config: ConferenceConfig, rows: AgendaRow[]): AgendaItem[] {
  const dayKey = new Map(config.days.map((d) => [d.date, d.key]));
  return rows.map((r, index) => {
    const kind = (KINDS as string[]).includes(r.kind) ? (r.kind as AgendaKind) : 'session';
    return {
      day: dayKey.get(r.date.slice(0, 10)) ?? config.days[0]?.key ?? '',
      sequence: index,
      title: r.title,
      description: r.description ?? undefined,
      start: hhmm(r.start),
      end: r.end ? hhmm(r.end) : undefined,
      kind: r.optionGroupId ? 'breakout' : r.question ? 'meal' : kind,
      slot: slotForGroup(config, r.optionGroupId),
      room: r.room ?? undefined,
      speakerKey: speakerKeyFor(config, r.speaker),
    };
  });
}

export function buildTopics(
  config: ConferenceConfig,
  details: EventDetailsResponse,
  speakers: Record<string, SpeakerView>
): TopicView[] {
  const mp = details.page?.topics ?? [];
  const byId = new Map<number, Topic>(mp.map((t) => [t.id, t]));
  const used = new Set<number>();
  const views: TopicView[] = [];
  const slotMeta = (groupId: number) => {
    const g = config.breakoutGroups.find((x) => x.groupId === groupId);
    return { slot: g?.slot ?? 0, label: g?.label ?? 'Breakout', timeHint: g?.agendaHint ?? '' };
  };
  const faceOf = (t: Topic | undefined): SpeakerView | null =>
    t?.speaker
      ? { name: t.speaker.name, tagline: '', imageGuid: null, imageSrc: t.speaker.imageUrl }
      : null;

  for (const c of config.topics) {
    const live = c.slots.map((s) => byId.get(s.optionPriceId)).filter((t): t is Topic => !!t);
    if (!live.length) continue; // Not offered in MP any more.
    live.forEach((t) => used.add(t.id));
    const first = live[0]!;
    const title = first.title || c.title;
    const halves = splitTitle(title);
    const configured = speakers[c.speakerKey];
    views.push({
      title,
      slug: c.slug,
      shortTitle: halves.shortTitle || c.shortTitle,
      subtitle: halves.subtitle ?? c.subtitle,
      blurb: first.description || c.blurb,
      panel: c.panel,
      room: first.room ?? c.room,
      speaker: configured
        ? { ...configured, imageSrc: configured.imageSrc ?? faceOf(first)?.imageSrc ?? null }
        : (faceOf(first) ?? { name: '', tagline: '', imageGuid: null, imageSrc: null }),
      replay: c.replay,
      slots: live.map((t) => ({
        ...slotMeta(t.groupId),
        capacity: t.capacity ?? 0,
        picked: t.capacity ? t.taken : null,
        optionPriceId: t.id,
        groupId: t.groupId,
      })),
    });
  }

  // Topics staff added in MP after the config was written: grouped by title.
  const extra = new Map<string, Topic[]>();
  for (const t of mp) {
    if (used.has(t.id)) continue;
    extra.set(t.title, [...(extra.get(t.title) ?? []), t]);
  }
  for (const [title, list] of extra) {
    const first = list[0]!;
    const halves = splitTitle(title);
    views.push({
      title,
      slug: `option-${first.id}`,
      shortTitle: halves.shortTitle,
      subtitle: halves.subtitle,
      blurb: first.description ?? '',
      room: first.room ?? undefined,
      speaker: faceOf(first) ?? { name: '', tagline: '', imageGuid: null, imageSrc: null },
      slots: list.map((t) => ({
        ...slotMeta(t.groupId),
        capacity: t.capacity ?? 0,
        picked: t.capacity ? t.taken : null,
        optionPriceId: t.id,
        groupId: t.groupId,
      })),
    });
  }
  return views;
}

/** slot -> topic slug, from one person's picks (option group -> option price). */
export function picksFor(person: ChoicePerson | null, topics: TopicView[]): Record<number, string> {
  const out: Record<number, string> = {};
  if (!person) return out;
  for (const topic of topics)
    for (const s of topic.slots)
      if (person.picks[s.groupId] === s.optionPriceId) out[s.slot] = topic.slug;
  return out;
}

export interface Meals {
  options: LunchOption[];
  caterer?: ConferenceConfig['caterer'];
  /** The form field the answer is written to. */
  formFieldId: number | null;
}

/**
 * The lunch menu: MP's answers, in MP's order, dressed with the config's photo
 * and words where the value matches. An answer the config never heard of
 * still shows, as itself.
 */
export function buildMeals(config: ConferenceConfig, rows: AgendaRow[]): Meals {
  const q = rows.find((r) => r.question)?.question ?? null;
  const dressed = (o: LunchOption): LunchOption => ({
    ...o,
    photo: o.photo ? art(o.photo) : o.photo,
  });
  const options = q?.choices.length
    ? q.choices.map((value) => {
        const known = config.lunchOptions.find((o) => o.value === value);
        return known ? dressed(known) : { value, label: value, detail: '' };
      })
    : config.lunchOptions.map(dressed);
  const caterer = config.caterer
    ? {
        ...config.caterer,
        logo: config.caterer.logo ? art(config.caterer.logo) : config.caterer.logo,
      }
    : undefined;
  return { options, caterer, formFieldId: q?.formFieldId ?? config.formFields.lunch ?? null };
}
